import { Link } from "react-router-dom";
import { BarChart2, Lock } from "lucide-react";
import { cn } from "@/lib/utils";
import { UnifiedCard } from "./UnifiedCard";

/**
 * F17 (AC#28): rendered instead of `<BenchmarkCharts>` and the offering-breakdown
 * table when `summary.benchmarksVisible === false` (Silver tier or no subscription).
 *
 * Gated on `benchmarksVisible` alone -- NEVER on `benchmarks.length === 0` (see
 * `TeacherAnalyticsPage.tsx`). An empty benchmarks array is also a legitimate state
 * for an entitled Gold/Platinum teacher whose cohort is below the minimum size, and
 * that case must render `CohortTooSmallNotice` below, not this upsell.
 */
export function BenchmarksUpsellCard() {
  return (
    <UnifiedCard icon={Lock} title="Benchmark Comparison">
      <div className="flex flex-col items-center gap-3 py-8 text-center">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100">
          <Lock className="h-5 w-5 text-slate-400" aria-hidden="true" />
        </div>
        <div className="max-w-sm">
          <p className="text-sm font-semibold text-slate-900">
            Benchmarks are a Gold &amp; Platinum benefit
          </p>
          <p className="mt-1 text-sm text-slate-500">
            See how your response rate, click-through rate, and inquiry volume compare to
            other teachers in your subject, plus a breakdown by offering. Upgrade to unlock.
          </p>
        </div>
        <Link
          to="/teacher-subscription"
          className={cn(
            "inline-flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white",
            "cursor-pointer motion-safe:transition-colors motion-safe:duration-200 hover:bg-slate-700",
            "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
          )}
        >
          Compare plans
        </Link>
      </div>
    </UnifiedCard>
  );
}

export interface CohortTooSmallNoticeProps {
  /** `summary.cohortSize` -- stays visible to every tier, entitled or not. */
  cohortSize: number;
}

/**
 * F17 (AC#28): rendered instead of `<BenchmarkCharts>` when a Gold/Platinum teacher
 * IS entitled (`benchmarksVisible === true`) but has no benchmark rows for the
 * currently-selected offering type because their subject cohort hasn't reached the
 * minimum size (10) for a reliable, anonymous comparison. This is explanation, not
 * an upsell -- `cohortSize` / `cohortMinimumMet` are never gated by tier.
 */
export function CohortTooSmallNotice({ cohortSize }: CohortTooSmallNoticeProps) {
  return (
    <UnifiedCard icon={BarChart2} title="Benchmark Comparison">
      <div className="py-8 text-center">
        <p className="text-sm text-slate-500">
          Your subject cohort has {cohortSize} teacher{cohortSize === 1 ? "" : "s"}. Benchmarks
          need at least 10 to keep comparisons reliable and anonymous — check back as more
          teachers join.
        </p>
      </div>
    </UnifiedCard>
  );
}

export default BenchmarksUpsellCard;
